import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

// MUI — per-component imports (project convention)
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Slide from "@mui/material/Slide";
import { DataGrid } from "@mui/x-data-grid";

// MUI Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PeopleIcon from "@mui/icons-material/People";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import PercentIcon from "@mui/icons-material/Percent";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

// App utilities
import { showErrorToast, showSuccessToast } from "../../../app/utils/helpers";
import ExcelManager from "../../../app/managers/excelManager";
import Langs, { Localize } from "../../../app/lang/langs";

// Redux thunks
import {
    fetchAwarenessTracking,
    exportAwarenessTracking,
    resetAcknowledgement,
} from "../vsecuriteSlice";

// ─── Brand colours ────────────────────────────────────────────────────────────
const ORANGE = "#FF9015";
const ORANGE_LIGHT = "rgba(255,144,21,0.10)";
const GREEN = "#4c8c2b";
const GREEN_LIGHT = "rgba(76,140,43,0.10)";
const GREY = "#757575";
const GREY_LIGHT = "rgba(0,0,0,0.05)";
const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// ─── Shared DataGrid sx ───────────────────────────────────────────────────────
const DATAGRID_SX = {
    borderRadius: "14px",
    border: "1px solid rgba(0,40,128,0.06)",
    "& .MuiDataGrid-columnHeaders": {
        background: "rgba(247,248,252,0.9)",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: 700,
        fontSize: "0.82rem",
        color: "#424242",
    },
    "& .MuiDataGrid-row": {
        transition: `background-color 200ms ${EASE}`,
    },
    "& .MuiDataGrid-row:hover": {
        background: "rgba(255,144,21,0.05)",
    },
    "& .MuiDataGrid-cell": {
        fontSize: "0.85rem",
        borderBottom: "1px solid rgba(0,40,128,0.04)",
    },
};

// ─── Status chip label helpers ────────────────────────────────────────────────

/**
 * Returns the chip label string for a given is_acknowledged value.
 * @param {number|boolean} isAcknowledged
 * @returns {string}
 */
// Note: getStatusLabel uses lang, so it's called as a function inside the component.
// We keep the function signature for use with a lang param.
const getStatusLabel = (isAcknowledged, lang) =>
    isAcknowledged ? Localize("VSEC_STATUS_READ_APPROVED", lang) : Localize("VSEC_STATUS_PENDING", lang);

// Resolves the display label for a tracking row's team column:
//   real team name → "— Individuel —" when individually targeted with no active team
//   → "Sans équipe" for orphan collabs
const getTeamDisplay = (row, lang) =>
    row?.team_name
        || (row?.is_individual_target
            ? Localize("VSEC_INDIVIDUAL_TARGET", lang)
            : Localize("VSEC_NO_TEAM_LABEL", lang));

/**
 * Returns the campaign status chip colour + label for the header.
 * @param {string} status - Campaign status string from the server.
 * @returns {{ label: string, color: string, bgColor: string }}
 */
const getCampaignStatusMeta = (status, lang) => {
    switch (status) {
        case "published":
            return { label: Localize("VSEC_CAMPAIGN_STATUS_PUBLISHED", lang), color: GREEN, bgColor: GREEN_LIGHT };
        case "draft":
            return {
                label: Localize("VSEC_CAMPAIGN_STATUS_DRAFT", lang),
                color: "#005670",
                bgColor: "rgba(0,86,112,0.10)",
            };
        case "archived":
            return { label: Localize("VSEC_CAMPAIGN_STATUS_ARCHIVED", lang), color: GREY, bgColor: GREY_LIGHT };
        default:
            return { label: status ?? "—", color: GREY, bgColor: GREY_LIGHT };
    }
};

// ─── Stat card sub-component ──────────────────────────────────────────────────

/**
 * Displays a single statistic (icon + value + label) in a styled card.
 * @param {{ icon: React.ReactNode, value: string|number, label: string, color: string, mobile: boolean }} props
 */
const StatCard = ({ icon, value, label, color, mobile }) => (
    <Box
        sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: mobile ? 2 : 2.5,
            py: 1.5,
            borderRadius: "14px",
            background: "rgba(255,255,255,0.75)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            border: "1px solid rgba(0,40,128,0.06)",
            boxShadow: "0 1px 4px rgba(255,144,21,0.04), 0 4px 16px rgba(0,40,128,0.03)",
            minWidth: mobile ? 0 : 160,
            flex: mobile ? 1 : "0 0 auto",
            transition: `all 300ms ${EASE}`,
            "&:hover": mobile ? {} : {
                transform: "translateY(-2px)",
                boxShadow: "0 6px 24px rgba(0,40,128,0.07), 0 2px 8px rgba(255,144,21,0.06)",
            },
        }}
    >
        <Box
            sx={{
                width: mobile ? 36 : 40,
                height: mobile ? 36 : 40,
                borderRadius: "12px",
                background: `${color}14`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: color,
                flexShrink: 0,
                transition: `background-color 250ms ${EASE}`,
            }}
        >
            {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: "#212121", letterSpacing: "-0.02em", fontSize: mobile ? "1rem" : undefined }}>
                {value}
            </Typography>
            <Typography variant="caption" sx={{ color: GREY, fontSize: mobile ? "0.68rem" : undefined, whiteSpace: "nowrap" }}>
                {label}
            </Typography>
        </Box>
    </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * AwarenessTracking — Admin dashboard for a single awareness campaign.
 *
 * Displays two tabs:
 *   1. "Par Collaborateur" — per-employee acknowledgement status with search,
 *      export, and reset actions.
 *   2. "Par Équipe" — team-level aggregates computed client-side with a
 *      completion progress bar per row.
 *
 * @param {{ campaign: object, onBack: () => void }} props
 *   campaign — object with { id, title, status }
 *   onBack   — callback invoked when the user presses the back button
 */
const AwarenessTracking = ({ campaign, onBack }) => {
    const dispatch = useDispatch();

    // ── Redux state ───────────────────────────────────────────────────────────
    const tracking = useSelector((state) => state.vsecurite.awareness.tracking);
    const sliceLoading = useSelector((state) => state.vsecurite.awareness.loading);
    const lang = useSelector((state) => state.settings.lang);
    const isMobile = useSelector((state) => state.settings.isMobile);

    const mobile = isMobile === 2;

    // ── Local state ───────────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState(0);
    const [localLoading, setLocalLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    // Confirmation dialog for "Réinitialiser"
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [targetRow, setTargetRow] = useState(null);
    const [resetLoading, setResetLoading] = useState(false);

    // Global filter for Tab 1 (Par Collaborateur)
    const [globalFilterValue, setGlobalFilterValue] = useState("");
    // Search for Tab 2 (Par Équipe)
    const [teamSearchValue, setTeamSearchValue] = useState("");

    const mounted = useRef(false);

    // ── Fetch on mount ────────────────────────────────────────────────────────

    /**
     * Loads the tracking data for the current campaign when the component mounts.
     */
    useEffect(() => {
        if (!mounted.current && campaign?.id) {
            setLocalLoading(true);
            dispatch(fetchAwarenessTracking({ id_campaign: campaign.id })).finally(() => {
                setLocalLoading(false);
            });
        }
        mounted.current = true;
    }, [campaign?.id]);

    // ── Computed stats ────────────────────────────────────────────────────────

    /**
     * Calculates total assigned, total acknowledged, and completion percentage
     * from the tracking array in Redux state.
     */
    const stats = useMemo(() => {
        const total = tracking?.length ?? 0;
        const approved = tracking?.filter((r) => r.is_acknowledged).length ?? 0;
        const pct = total > 0 ? Math.round((approved / total) * 100) : 0;
        return { total, approved, pct };
    }, [tracking]);

    // ── Team aggregation (Tab 2) ──────────────────────────────────────────────

    /**
     * Aggregates tracking rows by team_name to produce the team-level summary
     * table displayed in Tab 2. Computed client-side from Redux state.
     */
    const teamRows = useMemo(() => {
        if (!tracking?.length) return [];

        const map = {};
        tracking.forEach((row) => {
            const team = getTeamDisplay(row, lang);
            if (!map[team]) {
                map[team] = { team_name: team, total: 0, completed: 0 };
            }
            map[team].total += 1;
            if (row.is_acknowledged) map[team].completed += 1;
        });

        return Object.values(map).map((t) => ({
            ...t,
            pct: t.total > 0 ? Math.round((t.completed / t.total) * 100) : 0,
        }));
    }, [tracking, lang]);

    // ── Filtered rows (client-side) ───────────────────────────────────────────

    /**
     * Filters the collaborateur rows based on the global search text.
     * Searches across lastname, firstname, email, and team_name fields.
     */
    const filteredCollabRows = useMemo(() => {
        const rows = tracking ?? [];
        if (!globalFilterValue.trim()) return rows;
        const needle = globalFilterValue.toLowerCase();
        return rows.filter(
            (r) =>
                (r.lastname || "").toLowerCase().includes(needle) ||
                (r.firstname || "").toLowerCase().includes(needle) ||
                (r.email || "").toLowerCase().includes(needle) ||
                getTeamDisplay(r, lang).toLowerCase().includes(needle)
        );
    }, [tracking, globalFilterValue, lang]);

    /**
     * Filters the team rows based on the team search text.
     */
    const filteredTeamRows = useMemo(() => {
        if (!teamSearchValue.trim()) return teamRows;
        const needle = teamSearchValue.toLowerCase();
        return teamRows.filter((r) => r.team_name.toLowerCase().includes(needle));
    }, [teamRows, teamSearchValue]);

    // ── Event handlers ────────────────────────────────────────────────────────

    /**
     * Exports the tracking data to an Excel file using ExcelManager.
     * Falls back to a client-side export from current Redux state if the
     * server-side export thunk does not return binary data.
     */
    const handleExport = async () => {
        if (!tracking?.length) {
            showErrorToast(Localize("VSEC_TOAST_NO_DATA_EXPORT", lang));
            return;
        }
        setExportLoading(true);
        try {
            // Attempt server-side export first; if unavailable, fall back to
            // client-side generation from the tracking data already in state.
            await dispatch(exportAwarenessTracking({ id_campaign: campaign.id }));

            // Build the export rows from current state (always available)
            const exportData = tracking.map((row) => ({
                [Localize("VSEC_EXPORT_COL_LASTNAME", lang)]: row.lastname || "",
                [Localize("VSEC_EXPORT_COL_FIRSTNAME", lang)]: row.firstname || "",
                [Localize("VSEC_EXPORT_COL_EMAIL", lang)]: row.email || "",
                [Localize("VSEC_EXPORT_COL_TEAM", lang)]: getTeamDisplay(row, lang),
                [Localize("VSEC_EXPORT_COL_STATUS", lang)]: row.is_acknowledged ? Localize("VSEC_EXPORT_STATUS_READ", lang) : Localize("VSEC_EXPORT_STATUS_PENDING", lang),
                [Localize("VSEC_EXPORT_COL_APPROVAL_DATE", lang)]: row.acknowledged_at
                    ? moment(row.acknowledged_at).format("DD-MM-YYYY HH:mm")
                    : "",
            }));
            const filename = `suivi_sensibilisation_${campaign.id}_${moment().format("YYYYMMDD")}`;
            ExcelManager.getInstance().generateAndDownloadExcelFromData(exportData, filename);
            showSuccessToast(Localize("VSEC_TOAST_EXPORT_SUCCESS", lang));
        } catch (err) {
            showErrorToast(Localize("VSEC_TOAST_EXPORT_ERROR", lang));
        } finally {
            setExportLoading(false);
        }
    };

    /**
     * Opens the confirmation dialog before resetting a collaborator's
     * acknowledgement. Stores the target row in state so the dialog can
     * display the collaborator's name.
     * @param {object} rowData - The tracking row to reset.
     */
    const handleResetClick = (rowData) => {
        setTargetRow(rowData);
        setConfirmOpen(true);
    };

    /**
     * Closes the confirmation dialog and clears the target row without
     * performing any action.
     */
    const handleResetCancel = () => {
        setConfirmOpen(false);
        setTargetRow(null);
    };

    /**
     * Dispatches the resetAcknowledgement thunk for the target collaborator
     * after the admin confirms the action in the dialog. Refreshes tracking
     * data on success.
     */
    const handleResetConfirm = async () => {
        if (!targetRow) return;
        setResetLoading(true);
        try {
            const result = await dispatch(
                resetAcknowledgement({
                    id_campaign: campaign.id,
                    id_collab: targetRow.id_collab,
                })
            );
            if (result?.payload?.responseData?.status) {
                showSuccessToast(
                    `${Localize("VSEC_TOAST_RESET_SUCCESS", lang)} ${targetRow.firstname} ${targetRow.lastname}`
                );
                // Refresh tracking table
                dispatch(fetchAwarenessTracking({ id_campaign: campaign.id }));
            } else {
                showErrorToast(Localize("VSEC_TOAST_RESET_ERROR", lang));
            }
        } catch (err) {
            showErrorToast(Localize("VSEC_TOAST_RESET_ERROR2", lang));
        } finally {
            setResetLoading(false);
            setConfirmOpen(false);
            setTargetRow(null);
        }
    };

    // ── DataGrid column definitions ───────────────────────────────────────────

    /**
     * Column definitions for the "Par Collaborateur" DataGrid (Tab 1).
     * renderCell replaces PrimeReact Column body templates.
     */
    const collabColumns = useMemo(
        () => [
            {
                field: "lastname",
                headerName: Localize("VSEC_COL_LASTNAME", lang),
                minWidth: 130,
                flex: 1,
            },
            {
                field: "firstname",
                headerName: Localize("VSEC_COL_FIRSTNAME", lang),
                minWidth: 130,
                flex: 1,
            },
            {
                field: "email",
                headerName: Localize("VSEC_COL_EMAIL", lang),
                minWidth: 180,
                flex: 1.5,
            },
            {
                field: "team_name",
                headerName: Localize("VSEC_COL_TEAM", lang),
                minWidth: 130,
                flex: 1,
                valueGetter: (_, row) => getTeamDisplay(row, lang),
            },
            {
                field: "is_acknowledged",
                headerName: Localize("VSEC_COL_STATUS", lang),
                minWidth: 150,
                flex: 1,
                renderCell: (params) => {
                    const acknowledged = !!params.row.is_acknowledged;
                    return (
                        <Chip
                            icon={
                                acknowledged ? (
                                    <CheckCircleIcon sx={{ fontSize: 14 }} />
                                ) : (
                                    <HourglassEmptyIcon sx={{ fontSize: 14 }} />
                                )
                            }
                            label={getStatusLabel(acknowledged, lang)}
                            size="small"
                            sx={{
                                fontWeight: 600,
                                fontSize: "0.72rem",
                                color: acknowledged ? GREEN : GREY,
                                background: acknowledged ? GREEN_LIGHT : GREY_LIGHT,
                                border: `1px solid ${acknowledged ? GREEN : "#bdbdbd"}`,
                                "& .MuiChip-icon": { color: acknowledged ? GREEN : GREY },
                            }}
                        />
                    );
                },
            },
            {
                field: "acknowledged_at",
                headerName: Localize("VSEC_COL_APPROVAL_DATE", lang),
                minWidth: 160,
                flex: 1,
                renderCell: (params) => {
                    if (!params.row.acknowledged_at)
                        return <span style={{ color: GREY }}>—</span>;
                    return (
                        <span>
                            {moment(params.row.acknowledged_at).format("DD-MM-YYYY HH:mm")}
                        </span>
                    );
                },
            },
            {
                field: "_actions",
                headerName: Localize("VSEC_COL_ACTIONS", lang),
                minWidth: 100,
                sortable: false,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => {
                    if (!params.row.is_acknowledged) return null;
                    return (
                        <Tooltip
                            title={Localize("VSEC_TOOLTIP_RESET_APPROVAL", lang)}
                            placement="left"
                        >
                            <IconButton
                                size="small"
                                onClick={() => handleResetClick(params.row)}
                                sx={{
                                    color: "#d32f2f",
                                    minWidth: 44,
                                    minHeight: 44,
                                    "&:hover": { background: "rgba(211,47,47,0.08)" },
                                }}
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    );
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [lang]
    );

    /**
     * Column definitions for the "Par Équipe" DataGrid (Tab 2).
     */
    const teamColumns = useMemo(
        () => [
            {
                field: "team_name",
                headerName: Localize("VSEC_COL_TEAM", lang),
                minWidth: 180,
                flex: 1.5,
            },
            {
                field: "total",
                headerName: Localize("VSEC_COL_ASSIGNED_MEMBERS", lang),
                minWidth: 150,
                flex: 1,
            },
            {
                field: "completed",
                headerName: Localize("VSEC_COL_COMPLETED", lang),
                minWidth: 120,
                flex: 1,
            },
            {
                field: "pct",
                headerName: Localize("VSEC_COL_COMPLETION_RATE", lang),
                minWidth: 220,
                flex: 1.5,
                renderCell: (params) => (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            minWidth: 140,
                            width: "100%",
                        }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <LinearProgress
                                variant="determinate"
                                value={params.row.pct}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    background: GREY_LIGHT,
                                    "& .MuiLinearProgress-bar": {
                                        borderRadius: 4,
                                        background:
                                            params.row.pct === 100
                                                ? GREEN
                                                : params.row.pct >= 50
                                                ? ORANGE
                                                : "#d32f2f",
                                    },
                                }}
                            />
                        </Box>
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 700, minWidth: 34, color: "#212121" }}
                        >
                            {params.row.pct}%
                        </Typography>
                    </Box>
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [lang]
    );

    // ── Derived values ────────────────────────────────────────────────────────
    const isLoading = localLoading || sliceLoading;
    const campaignStatus = getCampaignStatusMeta(campaign?.status, lang);

    /* ── Mobile collab card ── */
    const renderMobileCollabCard = (row, idx) => {
        const acknowledged = !!row.is_acknowledged;
        return (
            <Card
                key={row.id_collab}
                elevation={0}
                sx={{
                    borderRadius: "12px",
                    border: "1px solid rgba(0,40,128,0.06)",
                    background: "rgba(255,255,255,0.85)",
                    mb: 1.5,
                    "@keyframes cardIn": {
                        from: { opacity: 0, transform: "translateY(8px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                    animation: `cardIn 300ms ${EASE} ${idx * 40}ms both`,
                }}
            >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    {/* Name + Status chip */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                        <Typography sx={{ fontWeight: 650, fontSize: "0.9rem", color: "#212121" }}>
                            {row.firstname} {row.lastname}
                        </Typography>
                        <Chip
                            icon={
                                acknowledged ? (
                                    <CheckCircleIcon sx={{ fontSize: 13 }} />
                                ) : (
                                    <HourglassEmptyIcon sx={{ fontSize: 13 }} />
                                )
                            }
                            label={getStatusLabel(acknowledged, lang)}
                            size="small"
                            sx={{
                                fontWeight: 600,
                                fontSize: "0.68rem",
                                color: acknowledged ? GREEN : GREY,
                                background: acknowledged ? GREEN_LIGHT : GREY_LIGHT,
                                border: `1px solid ${acknowledged ? GREEN : "#bdbdbd"}`,
                                "& .MuiChip-icon": { color: acknowledged ? GREEN : GREY },
                            }}
                        />
                    </Stack>

                    {/* Email */}
                    {row.email && (
                        <Typography sx={{ fontSize: "0.78rem", color: GREY, mb: 0.25 }}>
                            {row.email}
                        </Typography>
                    )}

                    {/* Team (or "individual target" fallback) */}
                    {(row.team_name || row.is_individual_target) && (
                        <Typography sx={{ fontSize: "0.75rem", color: "#999", mb: 0.5 }}>
                            {getTeamDisplay(row, lang)}
                        </Typography>
                    )}

                    {/* Acknowledged date + reset */}
                    {acknowledged && (
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                            <Typography sx={{ fontSize: "0.75rem", color: GREY }}>
                                {moment(row.acknowledged_at).format("DD-MM-YYYY HH:mm")}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => handleResetClick(row)}
                                sx={{
                                    color: "#d32f2f",
                                    minWidth: 44,
                                    minHeight: 44,
                                    "&:hover": { background: "rgba(211,47,47,0.08)" },
                                }}
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    )}
                </CardContent>
            </Card>
        );
    };

    /* ── Mobile team card ── */
    const renderMobileTeamCard = (row, idx) => {
        const barColor = row.pct === 100 ? GREEN : row.pct >= 50 ? ORANGE : "#d32f2f";
        return (
            <Card
                key={row.team_name}
                elevation={0}
                sx={{
                    borderRadius: "12px",
                    border: "1px solid rgba(0,40,128,0.06)",
                    background: "rgba(255,255,255,0.85)",
                    mb: 1.5,
                    "@keyframes cardIn": {
                        from: { opacity: 0, transform: "translateY(8px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                    animation: `cardIn 300ms ${EASE} ${idx * 40}ms both`,
                }}
            >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    <Typography sx={{ fontWeight: 650, fontSize: "0.9rem", color: "#212121", mb: 1 }}>
                        {row.team_name}
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                        <Box>
                            <Typography sx={{ fontSize: "0.68rem", color: "#999", fontWeight: 500 }}>
                                <Langs str="VSEC_COL_ASSIGNED_MEMBERS" />
                            </Typography>
                            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#212121" }}>
                                {row.total}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: "0.68rem", color: "#999", fontWeight: 500 }}>
                                <Langs str="VSEC_COL_COMPLETED" />
                            </Typography>
                            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#212121" }}>
                                {row.completed}
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{ flex: 1 }}>
                            <LinearProgress
                                variant="determinate"
                                value={row.pct}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    background: GREY_LIGHT,
                                    "& .MuiLinearProgress-bar": {
                                        borderRadius: 4,
                                        background: barColor,
                                    },
                                }}
                            />
                        </Box>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#212121", minWidth: 34 }}>
                            {row.pct}%
                        </Typography>
                    </Stack>
                </CardContent>
            </Card>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", mt: 0 }}>

            {/* ── Header bar ─────────────────────────────────────────────── */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: mobile ? 1.5 : 2.5,
                    py: 1.5,
                    background: "rgba(255,255,255,0.8)",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    borderBottom: "1px solid rgba(0,40,128,0.06)",
                    flexShrink: 0,
                    flexWrap: mobile ? "wrap" : "nowrap",
                }}
            >
                {/* Back button */}
                <Tooltip title={Localize("VSEC_TOOLTIP_BACK_CAMPAIGNS", lang)}>
                    <IconButton onClick={onBack} size="small" sx={{ color: ORANGE, minWidth: 44, minHeight: 44 }}>
                        <ArrowBackIcon />
                    </IconButton>
                </Tooltip>

                {/* Campaign title */}
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        color: "#212121",
                        flex: 1,
                        fontSize: mobile ? "0.95rem" : "1.05rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {campaign?.title ?? "Campagne"}
                </Typography>

                {/* Status chip */}
                <Chip
                    label={campaignStatus.label}
                    size="small"
                    sx={{
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        color: campaignStatus.color,
                        background: campaignStatus.bgColor,
                        border: `1px solid ${campaignStatus.color}`,
                    }}
                />
            </Box>

            {/* ── Tabs ───────────────────────────────────────────────────── */}
            <Box sx={{ borderBottom: "1px solid rgba(0,40,128,0.06)", flexShrink: 0, background: "rgba(255,255,255,0.7)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    variant={mobile ? "fullWidth" : "standard"}
                    sx={{
                        px: mobile ? 0 : 2,
                        "& .MuiTab-root": {
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: mobile ? "0.82rem" : "0.9rem",
                            minHeight: 48,
                            transition: `color 250ms ${EASE}`,
                            "&:hover": { color: "rgba(26,26,46,0.8)" },
                        },
                        "& .Mui-selected": { color: `${ORANGE} !important`, fontWeight: 700 },
                        "& .MuiTabs-indicator": { backgroundColor: ORANGE, height: 3, borderRadius: "3px 3px 0 0", boxShadow: "0 0 8px rgba(255,144,21,0.3)" },
                    }}
                >
                    <Tab label={Localize("VSEC_TAB_BY_COLLAB", lang)} />
                    <Tab label={Localize("VSEC_TAB_BY_TEAM", lang)} />
                </Tabs>
            </Box>

            {/* ── Body ───────────────────────────────────────────────────── */}
            <Box sx={{ flex: 1, overflow: "hidden", p: mobile ? 1.5 : 2, display: "flex", flexDirection: "column", gap: 2 }}>

                {/* Loading overlay */}
                {isLoading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress sx={{ color: ORANGE }} />
                    </Box>
                )}

                {!isLoading && activeTab === 0 && (
                    <>
                        {/* ── Stats row ─────────────────────────────────── */}
                        <Box sx={{ display: "flex", gap: mobile ? 1 : 2, flexWrap: "wrap" }}>
                            <StatCard
                                icon={<PeopleIcon fontSize="small" />}
                                value={stats.total}
                                label={Localize("VSEC_STAT_ASSIGNED", lang)}
                                color="#005670"
                                mobile={mobile}
                            />
                            <StatCard
                                icon={<DoneAllIcon fontSize="small" />}
                                value={stats.approved}
                                label={Localize("VSEC_STAT_APPROVED", lang)}
                                color={GREEN}
                                mobile={mobile}
                            />
                            <StatCard
                                icon={<PercentIcon fontSize="small" />}
                                value={`${stats.pct}%`}
                                label={Localize("VSEC_STAT_COMPLETION", lang)}
                                color={ORANGE}
                                mobile={mobile}
                            />
                        </Box>

                        {/* ── Toolbar: search + export ───────────────────── */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: mobile ? 1 : 2,
                                flexWrap: "wrap",
                                flexDirection: mobile ? "column" : "row",
                            }}
                        >
                            <TextField
                                size="small"
                                value={globalFilterValue}
                                onChange={(e) => setGlobalFilterValue(e.target.value)}
                                placeholder={Localize("VSEC_SEARCH_PLACEHOLDER", lang)}
                                fullWidth={mobile}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" sx={{ color: GREY }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    minWidth: mobile ? "100%" : 240,
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "12px",
                                        fontSize: "0.875rem",
                                        background: "rgba(0,40,128,0.025)",
                                        transition: `all 250ms ${EASE}`,
                                        "&.Mui-focused": {
                                            background: "rgba(255,255,255,0.9)",
                                            boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
                                        },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: ORANGE,
                                        },
                                    },
                                    "& input": { fontSize: "16px" },
                                }}
                            />
                            <Button
                                variant="contained"
                                size="small"
                                fullWidth={mobile}
                                startIcon={
                                    exportLoading ? (
                                        <CircularProgress size={14} sx={{ color: "#fff" }} />
                                    ) : (
                                        <FileDownloadIcon />
                                    )
                                }
                                disabled={exportLoading}
                                onClick={handleExport}
                                sx={{
                                    background: ORANGE,
                                    color: "#fff",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    borderRadius: "12px",
                                    minHeight: 48,
                                    boxShadow: "0 2px 8px rgba(255,144,21,0.25)",
                                    transition: `all 250ms ${EASE}`,
                                    "&:hover": { background: "#e07d00", boxShadow: "0 6px 20px rgba(255,144,21,0.35)", transform: "translateY(-1px) scale(1.02)" },
                                    "&:active": { transform: "scale(0.98)" },
                                    "&:disabled": { background: "#ffd09a", color: "#fff" },
                                }}
                            >
                                <Langs str="VSEC_BTN_EXPORT_EXCEL" />
                            </Button>
                        </Box>

                        {/* ── Desktop: DataGrid / Mobile: Card list ─── */}
                        {mobile ? (
                            <Box sx={{ overflowY: "auto", flex: 1, minHeight: 0, pb: 2 }}>
                                {filteredCollabRows.length === 0 && (
                                    <Box sx={{ textAlign: "center", py: 6 }}>
                                        <Typography sx={{ color: "rgba(0,0,0,0.4)", fontSize: "0.9rem" }}>
                                            <Langs str="VSEC_EMPTY_TRACKING" />
                                        </Typography>
                                    </Box>
                                )}
                                {filteredCollabRows.map((row, idx) => renderMobileCollabCard(row, idx))}
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    height: 'calc(100vh - 440px)',
                                    minHeight: 320,
                                    background: "rgba(255,255,255,0.8)",
                                    backdropFilter: "blur(6px)",
                                    WebkitBackdropFilter: "blur(6px)",
                                    borderRadius: "14px",
                                    border: "1px solid rgba(0,40,128,0.06)",
                                    boxShadow:
                                        "0 1px 4px rgba(255,144,21,0.04), 0 4px 16px rgba(0,40,128,0.03)",
                                    overflow: "hidden",
                                }}
                            >
                                <DataGrid
                                    rows={filteredCollabRows}
                                    columns={collabColumns}
                                    getRowId={(row) => row.id_collab}
                                    initialState={{
                                        pagination: { paginationModel: { pageSize: 25 } },
                                    }}
                                    pageSizeOptions={[10, 25, 50, 100]}
                                    disableRowSelectionOnClick
                                    localeText={{
                                        noRowsLabel: Localize("VSEC_EMPTY_TRACKING", lang),
                                    }}
                                    sx={DATAGRID_SX}
                                />
                            </Box>
                        )}
                    </>
                )}

                {!isLoading && activeTab === 1 && (
                    <>
                        {/* ── Team search bar ───────────────────────────── */}
                        <TextField
                            size="small"
                            value={teamSearchValue}
                            onChange={(e) => setTeamSearchValue(e.target.value)}
                            placeholder={Localize("VSEC_SEARCH_PLACEHOLDER", lang)}
                            fullWidth={mobile}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: GREY }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                minWidth: mobile ? "100%" : 240,
                                alignSelf: "flex-start",
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: mobile ? "12px" : "8px",
                                    fontSize: "0.875rem",
                                },
                                "& input": { fontSize: "16px" },
                            }}
                        />

                        {/* ── Desktop: DataGrid / Mobile: Card list ── */}
                        {mobile ? (
                            <Box sx={{ overflowY: "auto", flex: 1, minHeight: 0, pb: 2 }}>
                                {filteredTeamRows.length === 0 && (
                                    <Box sx={{ textAlign: "center", py: 6 }}>
                                        <Typography sx={{ color: "rgba(0,0,0,0.4)", fontSize: "0.9rem" }}>
                                            <Langs str="VSEC_EMPTY_TEAM_DATA" />
                                        </Typography>
                                    </Box>
                                )}
                                {filteredTeamRows.map((row, idx) => renderMobileTeamCard(row, idx))}
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    height: 'calc(100vh - 380px)',
                                    minHeight: 320,
                                    background: "rgba(255,255,255,0.8)",
                                    backdropFilter: "blur(6px)",
                                    WebkitBackdropFilter: "blur(6px)",
                                    borderRadius: "14px",
                                    border: "1px solid rgba(0,40,128,0.06)",
                                    boxShadow:
                                        "0 1px 4px rgba(255,144,21,0.04), 0 4px 16px rgba(0,40,128,0.03)",
                                    overflow: "hidden",
                                }}
                            >
                                <DataGrid
                                    rows={filteredTeamRows}
                                    columns={teamColumns}
                                    getRowId={(row) => row.team_name}
                                    initialState={{
                                        pagination: { paginationModel: { pageSize: 25 } },
                                    }}
                                    pageSizeOptions={[10, 25, 50]}
                                    disableRowSelectionOnClick
                                    localeText={{
                                        noRowsLabel: Localize("VSEC_EMPTY_TEAM_DATA", lang),
                                    }}
                                    sx={DATAGRID_SX}
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* ── Confirm reset dialog ────────────────────────────────────── */}
            <Dialog
                open={confirmOpen}
                onClose={handleResetCancel}
                fullScreen={mobile}
                TransitionComponent={mobile ? SlideUp : undefined}
                sx={{
                    zIndex: 1400,
                    ...(!mobile && {
                        "& .MuiDialog-paper": {
                            borderRadius: "18px",
                            minWidth: 360,
                            boxShadow: "0 16px 48px rgba(0,40,128,0.12), 0 4px 16px rgba(255,144,21,0.06)",
                        },
                    }),
                }}
                PaperProps={mobile ? {
                    sx: {
                        bgcolor: "#fff",
                        color: "#212121",
                    },
                } : undefined}
            >
                {mobile && (
                    <AppBar
                        position="sticky"
                        elevation={0}
                        sx={{
                            bgcolor: "#fff",
                            color: "#212121",
                            borderBottom: "1px solid rgba(0,40,128,0.06)",
                        }}
                    >
                        <Toolbar>
                            <IconButton edge="start" onClick={handleResetCancel} sx={{ color: "rgba(0,40,128,0.7)" }}>
                                <CloseIcon />
                            </IconButton>
                            <Typography sx={{ ml: 1, fontWeight: 700, fontSize: "1rem", flex: 1 }}>
                                <Langs str="VSEC_RESET_APPROVAL_TITLE" />
                            </Typography>
                        </Toolbar>
                    </AppBar>
                )}
                {!mobile && (
                    <DialogTitle sx={{ fontWeight: 700, color: "#212121" }}>
                        <Langs str="VSEC_RESET_APPROVAL_TITLE" />
                    </DialogTitle>
                )}
                <DialogContent sx={{ pt: mobile ? 3 : undefined }}>
                    <DialogContentText sx={{ color: "#424242" }}>
                        <Langs str="VSEC_RESET_APPROVAL_MSG_PRE" />{" "}
                        <strong>
                            {targetRow
                                ? `${targetRow.firstname} ${targetRow.lastname}`
                                : <Langs str="VSEC_RESET_APPROVAL_THIS_COLLAB" />}
                        </strong>{" "}
                        ? <Langs str="VSEC_RESET_APPROVAL_MSG_POST" />
                    </DialogContentText>
                </DialogContent>
                <DialogActions
                    sx={{
                        px: 3,
                        pb: 2,
                        gap: 1,
                        ...(mobile && {
                            position: "sticky",
                            bottom: 0,
                            bgcolor: "#fff",
                            borderTop: "1px solid rgba(0,40,128,0.06)",
                            flexDirection: "column",
                            p: 2,
                        }),
                    }}
                >
                    <Button
                        onClick={handleResetCancel}
                        disabled={resetLoading}
                        fullWidth={mobile}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            color: GREY,
                            minHeight: 48,
                            borderRadius: "12px",
                            border: mobile ? "1px solid rgba(0,0,0,0.12)" : "none",
                        }}
                    >
                        <Langs str="VSEC_BTN_CANCEL" />
                    </Button>
                    <Button
                        onClick={handleResetConfirm}
                        disabled={resetLoading}
                        variant="contained"
                        fullWidth={mobile}
                        startIcon={
                            resetLoading ? (
                                <CircularProgress size={14} sx={{ color: "#fff" }} />
                            ) : (
                                <RefreshIcon fontSize="small" />
                            )
                        }
                        sx={{
                            background: "#d32f2f",
                            color: "#fff",
                            fontWeight: 600,
                            textTransform: "none",
                            borderRadius: "12px",
                            minHeight: 48,
                            boxShadow: "0 2px 8px rgba(211,47,47,0.25)",
                            transition: `all 250ms ${EASE}`,
                            "&:hover": { background: "#b71c1c", boxShadow: "0 4px 16px rgba(211,47,47,0.3)", transform: "scale(1.02)" },
                            "&:active": { transform: "scale(0.98)" },
                            "&:disabled": { background: "#ef9a9a", color: "#fff" },
                        }}
                    >
                        <Langs str="VSEC_BTN_CONFIRM" />
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AwarenessTracking;
